import { useTheme } from 'app/shopify/theme';
import { INTERACTION } from 'app/shopify/generated/interaction';
import { ActionPressable, type ActionProps } from '../shared/ActionPressable';
import { ActionIconSlot, type ActionIcon } from '../shared/ActionContent';
import { iconButtonSize } from '../shared/button-presentation';
import { HeartFilledIcon } from './HeartFilledIcon';
import { HeartIcon } from './HeartIcon';

export interface ToggleIconButtonProps extends Omit<ActionProps, 'loading'> {
  selected?: boolean;
  type?: 'primary' | 'ghost' | 'secondaryNeutral';
  size?: 'small' | 'medium' | 'large';
  inverted?: boolean;
  icon?: ActionIcon;
  selectedIcon?: ActionIcon;
  onSelectedChange?: (selected: boolean) => void;
}

export function ToggleIconButton({
  selected = false,
  type = 'ghost',
  size = 'medium',
  inverted = false,
  icon,
  selectedIcon,
  onPress,
  onSelectedChange,
  ...props
}: ToggleIconButtonProps) {
  const theme = useTheme();
  const filled = type === 'ghost' && selected;
  const backgroundColor =
    type === 'primary'
      ? theme.colors['surface.default']
      : type === 'secondaryNeutral'
        ? theme.colors['component.button.bg.tertiary']
        : filled
          ? theme.colors['surface.brand.strong']
          : 'transparent';
  const color = theme.colors[filled || (inverted && type === 'ghost') ? 'icon.inverse.primary' : 'icon.brand'];
  const dimension = Number(theme.tokens.sizing[iconButtonSize[size]]);
  const iconSize = Number(theme.tokens.sizing[size === 'small' ? 'sizing.icon.sm' : 'sizing.icon.default']);
  return (
    <ActionPressable
      {...props}
      selected={selected}
      width={dimension}
      minHeight={dimension}
      onPress={(event) => {
        onPress?.(event);
        onSelectedChange?.(!selected);
      }}
      appearance={({ pressed, hovered, disabled }) => ({
        backgroundColor,
        opacity: disabled ? INTERACTION.opacity['040'] : 1,
        overlayColor:
          pressed || hovered
            ? theme.colors[
                filled
                  ? pressed
                    ? 'component.button.bg.pressed'
                    : 'component.button.bg.hover'
                  : type === 'secondaryNeutral'
                    ? pressed
                      ? 'state-layer.press'
                      : 'state-layer.hover'
                    : pressed
                      ? 'state-layer.press-brand'
                      : 'state-layer.hover-brand'
              ]
            : undefined,
      })}
    >
      {() => (
        <ActionIconSlot
          icon={selected ? (selectedIcon ?? icon ?? HeartFilledIcon) : (icon ?? HeartIcon)}
          size={iconSize}
          color={color}
        />
      )}
    </ActionPressable>
  );
}
